import { Box, Stack, StackProps, Tooltip, useTheme } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { LeafIcon } from '@synergy/theme-next';
import { PoolActionLabel, PoolRow } from '@synergy/components-next';
import { PoolMath } from '@synergy/utils';
import { ReactElement } from 'react';

import { ShoppingCartArticle, ShoppingCartItem } from '../../../../models/shopping-cart';

export interface SupplierTableRowProps extends StackProps {
    article: ShoppingCartArticle;
    netPrice: number;
    cartItem: ShoppingCartItem;
    quantity: number;
}

export const ArticlePriceMobile = (props: SupplierTableRowProps): ReactElement => {
    const theme = useTheme();
    const { article, cartItem, netPrice, quantity, ...stackProps } = props;

    return (
        <Stack typography="body2" {...stackProps}>
            <Stack direction="row" gap={0.5}>
                {article.isBestPrice && (
                    <Box>
                        <PoolActionLabel variant="small" color="secondary" hasSkew={false}>
                            <FormattedMessage id="app.filter.bestprice" />
                        </PoolActionLabel>
                    </Box>
                )}
                {article.isEcoProduct && (
                    <Box>
                        <Tooltip title={<FormattedMessage id="app.tooltip.ecoProduct" />}>
                            <Stack>
                                <PoolActionLabel variant="small" color="success" hasSkew={false}>
                                    <LeafIcon sx={{ fontSize: 16 }} />
                                </PoolActionLabel>
                            </Stack>
                        </Tooltip>
                    </Box>
                )}
            </Stack>

            <Box
                color={article.specialPrice ? theme.palette.error.dark : theme.palette.common.black}
                typography="h5"
                fontWeight={600}
            >
                <FormattedNumber
                    value={PoolMath.round(cartItem.netPrice * quantity)}
                    currency={article.currency}
                    style="currency"
                />
            </Box>

            <Box color={article.specialPrice ? theme.palette.error.dark : theme.palette.common.black}>
                <FormattedNumber value={netPrice} currency={article.currency} style="currency" />
                &nbsp;/&nbsp;
                {article.orderUnit}
            </Box>

            <Box>
                {article.specialPrice && (
                    <PoolRow gap={0.5}>
                        <Box sx={{ textDecoration: 'line-through' }}>
                            <FormattedNumber value={article.mainPrice} currency={article.currency} style="currency" />
                        </Box>
                        <Box color={theme.palette.error.dark}>
                            <FormattedNumber
                                value={-PoolMath.getPercentDifference(article.mainPrice, article.specialPrice.price)}
                                style="percent"
                            />
                        </Box>
                    </PoolRow>
                )}
            </Box>

            {article.contentUnit && (
                <Box color="grey.700">
                    (
                    <FormattedNumber
                        value={PoolMath.round(
                            (article.specialPrice?.price ?? article.mainPrice) / (article.contentQuantity ?? 1)
                        )}
                        currency={article.currency}
                        style="currency"
                    />
                    &nbsp;/&nbsp;
                    {article.contentUnit})
                </Box>
            )}
        </Stack>
    );
};
